import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import config from "../../config.json";
import { fetchAccounts } from "../../services/AccountService";
import {
  downloadPcapFile,
  exportPcapTraces,
  getPcapTraceStatus,
  IPcapTrace,
  IStartPcapTraceRequest,
  PcapTraceStatus,
  startPcapTrace,
  stopPcapTrace,
  usePcapTracesQuery,
  usePcapTraceStatusQuery,
} from "../../services/PcapTraceService";
import { IField, Option, Params } from "../../types/table/interfaces.type";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import DateFilterButtons from "../../components/DateFilterButtons";
import { Button } from "@mui/material";
import Actions from "../../components/common/Actions";
import { IAutoCompleteProps } from "../../components/common/AutoComplete";
import { format, parseISO } from "date-fns";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const ACTIVE_TRACES_STORAGE_KEY = "pcap_active_traces";

interface ActiveTrace {
  id: string;
  accountId: string;
  accountName: string;
  ipAddress: string;
  durationSeconds: number;
  localStartTime: number;
}

interface ActiveTraceCardProps {
  trace: ActiveTrace;
  onStop: (traceId: string) => void;
  isStopping: boolean;
  formatDuration: (seconds: number) => string;
  removeActiveTrace: (traceId: string) => void;
  queryClient: any;
  forceUpdateTrigger: number;
}

const ActiveTraceCard: React.FC<ActiveTraceCardProps> = ({
  trace,
  onStop,
  isStopping,
  formatDuration,
  removeActiveTrace,
  queryClient,
  forceUpdateTrigger,
}) => {
  const {
    data: traceData,
    error: traceError,
    isError: isTraceError,
  } = usePcapTraceStatusQuery(trace.id, {
    enabled: true,
    refetchInterval: 2000,
  });

  useEffect(() => {
    if (isTraceError && traceError) {
      if ((traceError as any)?.response?.status === 404) {
        removeActiveTrace(trace.id);
        queryClient.invalidateQueries({ queryKey: ["pcapTraces"] });
        ToastNotification.error(
          `PCAP trace for ${trace.accountName} not found. It may have been deleted.`
        );
      }
    } else if (traceData) {
      if (
        traceData.status === PcapTraceStatus.COMPLETED ||
        traceData.status === PcapTraceStatus.STOPPED ||
        traceData.status === PcapTraceStatus.FAILED
      ) {
        removeActiveTrace(trace.id);
        queryClient.invalidateQueries({ queryKey: ["pcapTraces"] });

        if (traceData.status === PcapTraceStatus.COMPLETED) {
          ToastNotification.success(
            `PCAP trace for ${trace.accountName} completed successfully!`
          );
        } else if (traceData.status === PcapTraceStatus.STOPPED) {
          ToastNotification.info(
            `PCAP trace for ${trace.accountName} stopped.`
          );
        } else {
          ToastNotification.error(
            `PCAP trace for ${trace.accountName} failed.`
          );
        }
      }
    }
  }, [
    traceData,
    isTraceError,
    traceError,
    trace.id,
    trace.accountName,
    removeActiveTrace,
    queryClient,
  ]);

  if (!traceData) {
    return (
      <Box
        sx={{
          mb: 2,
          p: 2,
          backgroundColor: "rgba(163, 148, 85, 0.1)",
          borderRadius: "8px",
          border: `1px solid ${COLORS.GOLD}`,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={20} sx={{ color: COLORS.GOLD }} />
        <CustomLabel
          text={`Loading trace for ${trace.accountName} (${trace.ipAddress})...`}
          fontSize="14px"
          fontWeight={500}
          color={COLORS.GOLD}
        />
      </Box>
    );
  }

  const _ = forceUpdateTrigger;
  const elapsed = (Date.now() - trace.localStartTime) / 1000;
  const remaining = Math.max(trace.durationSeconds - elapsed, 0);
  const progress = Math.min((elapsed / trace.durationSeconds) * 100, 100);

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: "rgba(163, 148, 85, 0.1)",
        borderRadius: "8px",
        border: `1px solid ${COLORS.GOLD}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <CustomLabel
            text={`${trace.accountName} - ${trace.ipAddress}`}
            fontSize="14px"
            fontWeight={600}
            color={COLORS.GOLD}
          />
          <Typography variant="caption" sx={{ color: COLORS.typographyColor }}>
            Duration: {formatDuration(trace.durationSeconds)} | Remaining:{" "}
            {formatDuration(Math.round(remaining))}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={() => onStop(trace.id)}
          disabled={isStopping || !hasJwtPermission("STOP_PCAP_TRACE")}
          startIcon={
            isStopping ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <StopIcon sx={{ width: 16, height: 16 }} />
            )
          }
          sx={{
            backgroundColor: COLORS.errorRed,
            color: "white",
            "&:hover": {
              backgroundColor: "#c62828",
            },
            textTransform: "none",
          }}
        >
          {isStopping ? "Stopping..." : "Stop"}
        </Button>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: "rgba(163, 148, 85, 0.2)",
          "& .MuiLinearProgress-bar": {
            backgroundColor: COLORS.GOLD,
            borderRadius: 4,
          },
        }}
      />
      <Typography
        variant="caption"
        sx={{ mt: 0.5, color: COLORS.GOLD, fontWeight: 500, display: "block" }}
      >
        Progress: {Math.round(progress)}%
      </Typography>
    </Box>
  );
};

const PcapTracePage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, setSearchParams] = useSearchParams();
  const [forceUpdate, setForceUpdate] = useState(0);

  const [appliedFilters, setAppliedFilters] = useState<
    Params<
      IPcapTrace & { fromDate?: string; toDate?: string; accountId?: string }
    >
  >({
    search: "",
    fromDate: "",
    toDate: "",
    accountId: "",
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedAccountFilter, setSelectedAccountFilter] =
    useState<string>("");

  const [accountOptions, setAccountOptions] = useState<Option[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [ipAddress, setIpAddress] = useState<string>("");
  const [durationSeconds, setDurationSeconds] = useState<number>(60);
  const [durationInput, setDurationInput] = useState<string>("60");

  const [activeTraces, setActiveTraces] = useState<ActiveTrace[]>(() => {
    const stored = localStorage.getItem(ACTIVE_TRACES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });
  const [, setRenderTrigger] = useState(0);
  const hasActiveTraces = activeTraces.length > 0;

  useEffect(() => {
    if (!hasActiveTraces) return;
    const interval = setInterval(() => {
      setForceUpdate((n) => n + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [hasActiveTraces]);

  const [isStarting, setIsStarting] = useState(false);
  const [stoppingTraceIds, setStoppingTraceIds] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const accountsPage = await fetchAccounts({
          sort: "name",
          size: config.MAX_PAGE_SIZE,
        });
        const accountsAsOptions = accountsPage.content.map((account: any) => {
          const ipAddress = account.acceptedIps || account.ip || "";
          return {
            label: `${account.name}${
              ipAddress ? ` (${ipAddress})` : " (No IP)"
            }`,
            value: String(account.id),
            ip: ipAddress,
          };
        });
        setAccountOptions(accountsAsOptions);

        // Auto-select account from URL params
        const accountIdFromUrl = searchParams.get("accountId");
        const ipFromUrl = searchParams.get("ip");
        if (accountIdFromUrl) {
          setSelectedAccountId(accountIdFromUrl);
          const selectedAccount = accountsAsOptions.find(
            (opt: any) => opt.value === accountIdFromUrl
          );
          if (selectedAccount && (selectedAccount as any).ip) {
            setIpAddress((selectedAccount as any).ip);
          } else if (ipFromUrl) {
            setIpAddress(decodeURIComponent(ipFromUrl));
          }
          // Clear the URL params after auto-selecting
          setSearchParams({}, { replace: true });
        }
      } catch (error) {
        console.error("Error loading accounts:", error);
      }
    };
    loadAccounts();
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (activeTraces.length > 0) {
      localStorage.setItem(
        ACTIVE_TRACES_STORAGE_KEY,
        JSON.stringify(activeTraces)
      );
    } else {
      localStorage.removeItem(ACTIVE_TRACES_STORAGE_KEY);
    }
  }, [activeTraces]);

  const removeActiveTrace = (traceId: string) => {
    setActiveTraces((prev) => prev.filter((t) => t.id !== traceId));
  };

  useEffect(() => {
    const today = new Date();
    const yyyy = today.getUTCFullYear();
    const mm = String(today.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(today.getUTCDate()).padStart(2, "0");
    const todayStr = `${yyyy}-${mm}-${dd}`;
    const tomorrow = new Date(today);
    tomorrow.setUTCDate(today.getUTCDate() + 1);
    const yyyy2 = tomorrow.getUTCFullYear();
    const mm2 = String(tomorrow.getUTCMonth() + 1).padStart(2, "0");
    const dd2 = String(tomorrow.getUTCDate()).padStart(2, "0");
    const tomorrowStr = `${yyyy2}-${mm2}-${dd2}`;
    handleDateFilterSelect(`${todayStr}_${tomorrowStr}`);
  }, []);

  const validateIpAddress = (ip: string): boolean => {
    const ipv4Regex =
      /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipv6Regex = /^(?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}$/;
    return ipv4Regex.test(ip) || ipv6Regex.test(ip);
  };

  const parseTimeToSeconds = (timeInput: string): number | null => {
    if (!timeInput.trim()) return null;

    // Remove extra spaces and convert to lowercase
    const input = timeInput.trim().toLowerCase();

    // If it's just a number, treat as seconds
    if (/^\d+$/.test(input)) {
      return parseInt(input);
    }

    let totalSeconds = 0;

    // Match hours (1h, 2 hours, etc.)
    const hoursMatch = input.match(/(\d+)\s*(?:h|hour|hours)/);
    if (hoursMatch) {
      totalSeconds += parseInt(hoursMatch[1]) * 3600;
    }

    // Match minutes (30m, 15 minutes, etc.)
    const minutesMatch = input.match(/(\d+)\s*(?:m|min|minute|minutes)/);
    if (minutesMatch) {
      totalSeconds += parseInt(minutesMatch[1]) * 60;
    }

    // Match seconds (30s, 45 seconds, etc.)
    const secondsMatch = input.match(/(\d+)\s*(?:s|sec|second|seconds)/);
    if (secondsMatch) {
      totalSeconds += parseInt(secondsMatch[1]);
    }

    // If no units found but contains numbers, try to parse as minutes:seconds format (e.g., "2:30")
    if (totalSeconds === 0 && input.includes(":")) {
      const parts = input.split(":");
      if (parts.length === 2) {
        const minutes = parseInt(parts[0]);
        const seconds = parseInt(parts[1]);
        if (!isNaN(minutes) && !isNaN(seconds)) {
          totalSeconds = minutes * 60 + seconds;
        }
      }
    }

    return totalSeconds > 0 ? totalSeconds : null;
  };

  const handleStartTrace = async () => {
    if (!selectedAccountId) {
      ToastNotification.warning("Please select an account.");
      return;
    }
    if (!ipAddress) {
      ToastNotification.warning("Please enter a valid IP address.");
      return;
    }

    let finalDuration: number;
    if (/^\d+$/.test(durationInput)) {
      finalDuration = parseInt(durationInput);
    } else {
      const parsedDuration = parseTimeToSeconds(durationInput);
      if (parsedDuration === null) {
        ToastNotification.warning(
          "Invalid duration format. Use formats like '2m', '1h30m', '90s', or '2:30'."
        );
        return;
      }
      finalDuration = parsedDuration;
    }

    if (finalDuration < 10 || finalDuration > 3600) {
      ToastNotification.warning(
        "Duration must be between 10 and 3600 seconds."
      );
      return;
    }

    setIsStarting(true);
    try {
      const request: IStartPcapTraceRequest = {
        accountId: selectedAccountId,
        ipAddress,
        durationSeconds: finalDuration,
      };
      const response = await startPcapTrace(request);

      const selectedAccount = accountOptions.find(
        (opt) => opt.value === selectedAccountId
      );
      const newTrace: ActiveTrace = {
        id: response.id,
        accountId: selectedAccountId,
        accountName: selectedAccount?.label.split(" (")[0] || "Unknown",
        ipAddress,
        durationSeconds: finalDuration,
        localStartTime: Date.now(),
      };

      setActiveTraces((prev) => [...prev, newTrace]);
      setSelectedAccountId("");
      setIpAddress("");
      setDurationInput("60");
      ToastNotification.success("PCAP trace started successfully!");
      queryClient.invalidateQueries({ queryKey: ["pcapTraces"] });
    } catch (error: any) {
      ToastNotification.error(error.message || "Failed to start PCAP trace.");
    } finally {
      setIsStarting(false);
    }
  };

  const handleStopTrace = async (traceId: string) => {
    setStoppingTraceIds((prev) => new Set(prev).add(traceId));
    try {
      await stopPcapTrace(traceId);
      removeActiveTrace(traceId);
      ToastNotification.info("PCAP trace stopped.");
      queryClient.invalidateQueries({ queryKey: ["pcapTraces"] });
    } catch (error: any) {
      ToastNotification.error(error.message || "Failed to stop PCAP trace.");
    } finally {
      setStoppingTraceIds((prev) => {
        const next = new Set(prev);
        next.delete(traceId);
        return next;
      });
    }
  };

  const handleDownload = async (traceId: string, filePath?: string) => {
    try {
      let finalFilePath = filePath;
      if (!finalFilePath) {
        try {
          const traceData = await getPcapTraceStatus(traceId);
          finalFilePath = traceData.filePath;
        } catch (error) {
          console.warn("Could not fetch trace status for filename:", error);
        }
      }

      await downloadPcapFile(traceId, finalFilePath);
      ToastNotification.success("Download started.");
    } catch (error: any) {
      ToastNotification.error(error.message || "Failed to download file.");
    }
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate ? fromDate.toISOString() : "",
      toDate: toDate ? toDate.toISOString() : "",
      accountId: selectedAccountFilter,
    }));
  };

  const handleExport = async () => {
    try {
      await exportPcapTraces(appliedFilters);
      ToastNotification.success("Export completed.");
    } catch (error: any) {
      ToastNotification.error(error.message || "Failed to export.");
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getStatusColor = (status: PcapTraceStatus): string => {
    switch (status) {
      case PcapTraceStatus.RUNNING:
        return COLORS.GOLD;
      case PcapTraceStatus.COMPLETED:
        return COLORS.successGreen;
      case PcapTraceStatus.STOPPED:
        return COLORS.WarningOrange;
      case PcapTraceStatus.FAILED:
        return COLORS.errorRed;
      case PcapTraceStatus.PENDING:
      default:
        return COLORS.typographyColor;
    }
  };

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return "-";
    try {
      const date = parseISO(dateString);
      return format(date, "dd-MM-yyyy hh:mm:ss a");
    } catch {
      return dateString;
    }
  };

  const accountFilterOptions: Option[] = useMemo(
    () =>
      accountOptions.map((opt) => ({
        label: opt.label.split(" (")[0],
        value: opt.value,
      })),
    [accountOptions]
  );

  const fields: IField<IPcapTrace>[] = [
    {
      key: "accountName",
      header: "Account",
    },
    {
      key: "ipAddress",
      header: "IP Address",
    },
    {
      key: "durationSeconds",
      header: "Duration",
    },
    {
      key: "status",
      header: "Status",
      transform: (row: IPcapTrace) => {
        const _ = forceUpdate;
        if (row.status === PcapTraceStatus.RUNNING) {
          const activeTrace = activeTraces.find((t) => t.id === row.id);
          if (activeTrace) {
            const elapsed = (Date.now() - activeTrace.localStartTime) / 1000;
            const progress = Math.min(
              (elapsed / activeTrace.durationSeconds) * 100,
              100
            );
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    width: "100%",
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "rgba(163, 148, 85, 0.2)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: COLORS.GOLD,
                      borderRadius: 3,
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, color: COLORS.GOLD, fontWeight: 500 }}
                >
                  {row.status} ({Math.round(progress)}%)
                </Typography>
              </Box>
            );
          }
          // No localStartTime available trace started elsewhere, show indeterminate
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <LinearProgress
                variant="indeterminate"
                sx={{
                  width: "100%",
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "rgba(163, 148, 85, 0.2)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: COLORS.GOLD,
                    borderRadius: 3,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ mt: 0.5, color: COLORS.GOLD, fontWeight: 500 }}
              >
                {row.status}
              </Typography>
            </Box>
          );
        }
        if (
          row.status === PcapTraceStatus.COMPLETED &&
          row.startedAt &&
          (row.stoppedAt || row.stopAt)
        ) {
          // Use server timestamps only
          const startTime = new Date(row.startedAt).getTime();
          const stopTime = new Date(row.stoppedAt || row.stopAt!).getTime();
          const elapsed = (stopTime - startTime) / 1000;
          const progress = Math.min((elapsed / row.durationSeconds) * 100, 100);
          const statusColor = getStatusColor(row.status);
          return (
            <Typography
              variant="body2"
              sx={{ color: statusColor, fontWeight: 500 }}
            >
              {row.status} ({Math.round(progress)}%)
            </Typography>
          );
        }
        const statusColor = getStatusColor(row.status);
        return (
          <Typography
            variant="body2"
            sx={{ color: statusColor, fontWeight: 500 }}
          >
            {row.status}
          </Typography>
        );
      },
    },
    {
      key: "startedAt",
      header: "Started At",
      transform: (row: IPcapTrace) => formatDateTime(row.startedAt),
    },
    {
      key: "stopAt",
      header: "Stop At",
      transform: (row: IPcapTrace) => formatDateTime(row.stopAt),
    },
    {
      key: "stoppedAt",
      header: "Stopped At",
      transform: (row: IPcapTrace) => formatDateTime(row.stoppedAt),
    },
    {
      key: "fileSize",
      header: "File Size",
      transform: (row: IPcapTrace) => formatFileSize(row.fileSize),
    },
    {
      key: "filePath",
      header: "File Name",
    },
    {
      key: "createdBy",
      header: "Created By",
    },
  ];

  const actions = Actions({
    onDownload: {
      handler: (id: string | number, row?: IPcapTrace) => {
        handleDownload(String(id), row?.filename);
      },
      isDisabled: (row: IPcapTrace) => {
        return (
          (row.status !== PcapTraceStatus.COMPLETED &&
            row.status !== PcapTraceStatus.STOPPED) ||
          !row.fileSize ||
          !hasJwtPermission("DOWNLOAD_PCAP_TRACE")
        );
      },
    },
  });

  const durationOptions: Option[] = [
    { label: "30 seconds", value: "30" },
    { label: "1 minute", value: "60" },
    { label: "2 minutes", value: "120" },
    { label: "5 minutes", value: "300" },
    { label: "10 minutes", value: "600" },
    { label: "15 minutes", value: "900" },
    { label: "30 minutes", value: "1800" },
    { label: "1 hour", value: "3600" },
  ];

  const handleDateFilterSelect = (option: string) => {
    if (option) {
      const [startStr, endStr] = option.split("_");
      setFromDate(new Date(startStr));
      setToDate(new Date(endStr));
    } else {
      setFromDate(null);
      setToDate(null);
    }
  };

  const handleFromDateChange = (
    date: Date | null | ((prevState: Date | null) => Date | null)
  ) => {
    const newDate = typeof date === "function" ? date(fromDate) : date;
    setFromDate(newDate);
  };

  const handleToDateChange = (
    date: Date | null | ((prevState: Date | null) => Date | null)
  ) => {
    const newDate = typeof date === "function" ? date(toDate) : date;
    setToDate(newDate);
  };

  const useFetchPcapTraces = (params: Params<IPcapTrace>) =>
    usePcapTracesQuery(params, { refetchInterval: 5000 });

  const activeIpAddressesKey = activeTraces
    .map((t) => t.ipAddress)
    .sort((a, b) => a.localeCompare(b))
    .join(",");
  const filteredAccountOptions = useMemo(() => {
    const activeIpAddresses = new Set(activeIpAddressesKey.split(",").filter(Boolean));
    return accountOptions.map((opt) => ({
      ...opt,
      disabled: activeIpAddresses.has((opt as any).ip),
    }));
  }, [accountOptions, activeIpAddressesKey]);

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="PCAP Trace History"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />
        <SearchForm
          autoCompletes={[
            {
              label: "Account Name",
              value: selectedAccountFilter,
              options: accountFilterOptions,
              onChange: (value: string | number | (string | number)[]) => {
                setSelectedAccountFilter(String(value));
              },
              placeholder: "Filter by Account",
              clearable: true,
            },
          ]}
          dateComponent={
            <Box display="flex" flexDirection="column" gap={2} width="100%">
              <DateFilterButtons onSelect={handleDateFilterSelect} />
              <CustomDateRangePicker
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={handleFromDateChange}
                setToDate={handleToDateChange}
              />
            </Box>
          }
          buttons={[
            {
              label: "Search",
              bgColor: COLORS.GOLD,
              textColor: "white",
              onClick: handleSearchClick,
              permission: "GET_ALL_PCAP_TRACES",
            },
          ]}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Start PCAP Trace"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />

        <SearchForm
          autoCompletes={[
            {
              label: "Account",
              options: filteredAccountOptions.filter((opt) => !opt.disabled),
              value: selectedAccountId,
              onChange: (value: string | number | (string | number)[]) => {
                const accountId = String(value);
                setSelectedAccountId(accountId);
                const selectedAccount = accountOptions.find(
                  (opt) => opt.value === accountId
                );
                if (selectedAccount && (selectedAccount as any).ip) {
                  setIpAddress((selectedAccount as any).ip);
                }
              },
              placeholder: "Select Account",
            },
            {
              label: "Duration",
              options: durationOptions,
              value: durationInput,
              onChange: (value: string | number | (string | number)[]) => {
                const stringValue = String(value);
                setDurationInput(stringValue);
                if (/^\d+$/.test(stringValue)) {
                  setDurationSeconds(Number(stringValue));
                }
              },
              onInputChange: (
                event: React.SyntheticEvent,
                inputValue: string
              ) => {
                setDurationInput(inputValue);
                if (/^\d+$/.test(inputValue)) {
                  setDurationSeconds(Number(inputValue));
                }
              },
              placeholder: "e.g., 2m, 1h, 90s",
              freeSolo: true,
              getOptionLabel: (
                option: string | { label: string; value: string | number }
              ) => {
                if (typeof option === "string") {
                  return option;
                }
                return String(option.label || option.value || "");
              },
            },
          ]}
          buttons={[
            {
              label: isStarting ? "Starting..." : "Start Trace",
              bgColor: COLORS.GOLD,
              textColor: "white",
              onClick: handleStartTrace,
              disabled: isStarting || !selectedAccountId || !hasJwtPermission("START_PCAP_TRACE"),
              startIcon: isStarting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PlayArrowIcon sx={{ width: 16, height: 16 }} />
              ),
            },
          ]}
        />

        {activeTraces.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <CustomLabel
              text="Active Traces"
              variant="subtitle1"
              fontWeight={600}
              marginBottom={2}
            />
            {activeTraces.map((trace) => (
              <ActiveTraceCard
                key={trace.id}
                trace={trace}
                onStop={handleStopTrace}
                isStopping={stoppingTraceIds.has(trace.id)}
                formatDuration={formatDuration}
                removeActiveTrace={removeActiveTrace}
                queryClient={queryClient}
                forceUpdateTrigger={forceUpdate}
              />
            ))}
          </Box>
        )}
      </Box>

      <SearchHeaderContainer
        search={appliedFilters.search ?? ""}
        setSearch={(value) =>
          setAppliedFilters((prev) => ({ ...prev, search: value }))
        }
        buttonConfig={[
          {
            size: "small",
            bgColor: COLORS.GOLD,
            startIcon: <Export />,
            onClick: handleExport,
            showExportProgress: true,
            label: "Export",
            textColor: COLORS.iconColor,
            permission: "GET_ALL_PCAP_TRACES_CSV",
          },
        ]}
      />

      <CustomTable<IPcapTrace>
        filters={appliedFilters}
        fields={fields}
        fetchFunc={useFetchPcapTraces}
        actions={actions}
      />
    </Box>
  );
};

export default PcapTracePage;
